import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { ILineResource } from '../../entities/farms.entities';
import {
  Button,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  Subtitle,
} from '../shared';
import { useDispatch, useSelector } from 'react-redux';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import { showFeedback } from '../../store/ui/ui.actions';
import { IInventoryResource } from '../../entities/inventory.entities';
import { sendSingleRequest } from '../../apis';
import moment from 'moment';
import { numToStr, toMillisecond, toSecond } from '../../lib/common.helpers';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../shared/input/TimeTrackInput';
import { loadFarmsData, loadQueueData } from '../../store/farms/farms.actions';
import { ITimeRange } from '../../entities/general.entities';
import { ICycleResource } from '../../entities/growing.entities';
import { loadInventories } from '../../store/inventories/inventories.actions';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  SelectFarmsByFarmID,
  SelectInventoriesByFarmID,
} from '../../store/extra/extra.selector';
import { selectProfile } from '../../store/auth/auth.selector';
import { AreaClosedConfirmModal } from '../CommonViews';
import SelectBoat from '../farmform/SelectBoat';
import { TaggingManageForm } from '../lines/LineTagButton';
import { loadTags } from '../../store/automation/automation.actions';
import SelectBatchSeed from '../farmform/SelectBatchSeed';

interface IForm {
  type: 'IN' | 'OUT';
  quantity?: number;
  manage_date: number;
  batch_seed_id?: number | null;
  boat_id?: number | null;
  time_tracks?: ITimeRange[];

  tagged_ids?: number[] | null;
}

interface IData {
  seeding_id?: number;
  inventory_id: number;
  quantity: number;
  status: 'IN' | 'OUT';
  type?: 'IN' | 'OUT';
  manage_date?: number;
  batch_seed_id?: number | null;
  boat_id?: number | null;
  time_tracks?: ITimeRange[];

  tagged_ids?: number[] | null;
}

interface Props {
  visible: boolean;
  className?: string;
  title: string;
  data?: IData;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  lineData: ILineResource;
  onlyView?: boolean;
  updateId?: number;
  overConfirm?: (data: any) => void;
  lineChangeable?: boolean;
  manageCycle?: ICycleResource;
}

const FloatManageModal = ({
  visible,
  className,
  title,
  data,
  onCancel,
  onConfirm,
  lineData,
  onlyView,
  updateId,
  overConfirm,
  lineChangeable,
  manageCycle,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);

  const typeOptions = useMemo(
    () => [
      { id: 'IN', value: 'IN', label: translate(lang, '_remove_line_inv') },
      { id: 'OUT', value: 'OUT', label: translate(lang, 'Added to the line') },
    ],
    [lang],
  );

  const inventories = SelectInventoriesByFarmID(lineData.farm_id);
  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const curBoat = useSelector(selectProfile)?.current_boat;

  const lastSubmersion = useMemo(() => {
    if (!manageCycle?.seed_manages) {
      return null;
    }
    if (manageCycle.seed_manages.length <= 0) {
      return manageCycle.main_seed.submersion;
    }
    const arr = [...manageCycle.seed_manages];
    arr.sort((a, b) => b.manage_date - a.manage_date);

    return arr[0].submersion;
  }, [manageCycle]);

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [curIvt, setCurIvt] = useState<IInventoryResource>();
  const [formData, setFormData] = useState<IForm>({
    type: data?.status ?? data?.type ?? 'IN',
    quantity: data?.quantity,
    manage_date: toMillisecond(data?.manage_date),
    batch_seed_id: data?.batch_seed_id ?? null,
    boat_id: data?.boat_id ?? curBoat?.id,
    time_tracks:
      data?.time_tracks?.map(x => ({
        start_time: toMillisecond(x.start_time),
        finish_time: toMillisecond(x.finish_time),
      })) ?? [],
    tagged_ids: data?.tagged_ids ?? null,
  });
  const [curFarm, setCurFarm] = useState(
    farmsData.find(x => x.id === lineData.farm_id),
  );
  const [curLine, setCurLine] = useState(lineData);
  const [depth, setDepth] = useState(lastSubmersion);

  const { ivtFloats, mainSeed } = useMemo(
    () => ({
      ivtFloats: curLine.growing_cycle?.inventories_sum,
      mainSeed: curLine.growing_cycle?.main_seed,
    }),
    [curLine],
  );
  const availableQty = useMemo(() => {
    if (!curIvt) return 0;
    if (formData.type === 'OUT') {
      return curIvt.available_quantity;
    } else {
      return ivtFloats?.find(x => x.inventory_id === curIvt.id)?.quantity ?? 0;
    }
  }, [curIvt, ivtFloats, formData.type]);

  const handleLineChange = (v: string) => {
    if (!curFarm) return;
    const line = curFarm.lines.find(x => x.id === Number(v));
    if (!line?.growing_cycle) return;
    setCurLine(line);
  };
  const selectInventory = (id: string) => {
    const ivt = inventories.find(x => x.id === Number(id));
    setCurIvt(ivt);
  };
  const updateForm = (key: keyof IForm, value: any) => {
    if (key === 'quantity') {
      value = value.length <= 0 ? undefined : Number(value);
    }
    setFormData({ ...formData, [key]: value });
  };

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        type: 'error',
        message: translate(lang, message),
      }),
    );

  const submitLineDepth = async () => {
    if (!manageCycle || !depth || Number(depth) === Number(lastSubmersion)) {
      return false;
    }
    const form = {
      seeding_id: manageCycle.main_seed.id,
      manage_date: toSecond(moment().toDate().getTime()),
      submersion: depth,
    };
    const res = await sendSingleRequest(
      form,
      'POST',
      'api/farm/line/seed/manage',
      true,
    );
    if (res.status) {
      alert(translate(lang, '_line_depth_saved_ok'));
    } else {
      alert(translate(lang, res.data?.message ?? '_failed_save_line_depth'));
    }
    return true;
  };

  const validForm = () => {
    if (!curIvt) {
      return '_sel_inv';
    }
    if (
      formData.quantity === undefined ||
      formData.quantity === null ||
      formData.quantity < 0 ||
      formData.quantity > availableQty
    ) {
      return formData.quantity === undefined || formData.quantity === null
        ? '_input_qty'
        : formData.quantity < 0
          ? '_qty_must_greater_than'
          : translate(lang, '_float_no_enough');
    }
    const time_tracks = formData.time_tracks
      ? toSecTimeTracks(formData.time_tracks)
      : [];
    if (time_tracks === false) {
      return translate(lang, '_invalid_time_range');
    }
    const seeding_id =
      lineData.id === curLine.id
        ? (data?.seeding_id ?? mainSeed?.id)
        : mainSeed?.id;
    if (!seeding_id) {
      return '_line_growing';
    }
    return {
      inventory_id: curIvt.id,
      ...formData,
      manage_date: toSecond(formData.manage_date),
      time_tracks,
      seeding_id,
    };
  };
  const handleConfirmClick = async () => {
    const form = validForm();
    if (typeof form === 'string') {
      setDisabled(true);
      const dr = await submitLineDepth();
      setDisabled(false);
      if (!dr) {
        showError(form);
      } else {
        onConfirm(dr);
      }
      return;
    }
    if (!form.quantity) {
      setDisabled(true);
      const dr = await submitLineDepth();
      setDisabled(false);
      if (dr) {
        onConfirm(dr);
      } else {
        onCancel();
      }
      return;
    }
    setDisabled(true);
    if (overConfirm) {
      overConfirm(form);
      return;
    }
    const res = !updateId
      ? await sendSingleRequest(
          form,
          'POST',
          `api/farm/line/seed/inventory-seeds/${mainSeed?.id}`,
          true,
        )
      : await sendSingleRequest(
          {
            floating_id: updateId,
            quantity: form.quantity,
            batch_seed_id: form.batch_seed_id,
            boat_id: form.boat_id,
            manage_date: form.manage_date,
            time_tracks: form.time_tracks,
          },
          'PUT',
          'api/farm/line/seed/inventory-seeds',
          true,
        );
    if (res.status) {
      if (form.tagged_ids) {
        dispatch(loadTags());
      }
      await submitLineDepth();
      dispatch(loadFarmsData());
      dispatch(loadInventories());
      dispatch(loadQueueData());
      onConfirm(res.data);
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? '_floats_added_ok'),
        }),
      );
    } else {
      showError(res.data?.message ?? 'Failed');
      setDisabled(false);
    }
  };

  useEffect(() => {
    const tmp = data ? inventories.find(x => x.id === data.inventory_id) : null;
    if (data && tmp) {
      setCurIvt(tmp);
    }
  }, [data, inventories]);

  if (!isConfirmed) {
    return (
      <AreaClosedConfirmModal
        farmId={lineData.farm_id}
        onCancel={onCancel}
        onConfirm={() => setIsConfirmed(true)}
      />
    );
  }

  const farmOptions = farmsData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.name} - ${x.farm_number}`,
  }));
  const lineOptions = curFarm?.lines
    .filter(x => !!x.growing_cycle)
    .map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: x.line_name,
    }));
  const inventoriesOption = inventories.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: x.name,
  }));

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      className={className}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, title)}
          </Subtitle>
        </div>
        <div>
          {lineChangeable && (
            <div className='mb-17 d-flex'>
              <div className='w-100 mr-7'>
                <Dropdown
                  label={translate(lang, 'Select farm')}
                  options={farmOptions}
                  value={curFarm?.id.toString()}
                  onChange={v =>
                    setCurFarm(farmsData.find(x => x.id === Number(v)))
                  }
                />
              </div>
              <div className='w-100 ml-7'>
                <Dropdown
                  label={translate(lang, 'Select line')}
                  options={lineOptions ?? []}
                  value={
                    !!lineOptions &&
                    lineOptions.some(x => Number(x.id) === curLine.id)
                      ? curLine.id.toString()
                      : curLine.line_name
                  }
                  onChange={handleLineChange}
                />
              </div>
            </div>
          )}
          <div className='mb-16 d-flex'>
            <div className='mr-7 w-100'>
              <Datepicker
                label={translate(lang, 'Manage date')}
                defaultValue={formData.manage_date}
                onChange={e =>
                  updateForm(
                    'manage_date',
                    e?.toDate().getTime() ?? moment().toDate().getTime(),
                  )
                }
                required
                disabled={onlyView}
              />
            </div>
            <div className='ml-7 w-100'>
              <Dropdown
                label={translate(lang, 'Select inventory')}
                options={inventoriesOption}
                value={curIvt?.id.toString()}
                onChange={x => selectInventory(x)}
                disabled={onlyView || !!updateId}
              />
            </div>
          </div>
          <div className='mb-16'>
            <SelectBatchSeed
              label={translate(lang, 'This is seed specific management')}
              lineData={curLine}
              seedings={curLine.growing_cycle?.seedings ?? []}
              value={formData.batch_seed_id}
              onChange={v => updateForm('batch_seed_id', v)}
            />
          </div>
          {!!curIvt && (
            <div className='mb-16 d-flex'>
              <div className='mr-7 w-100'>
                <Dropdown
                  label={translate(lang, 'Type')}
                  options={typeOptions}
                  value={formData.type}
                  onChange={v => updateForm('type', v)}
                  disabled={onlyView || !!updateId}
                />
              </div>
              <div className='ml-7 w-100'>
                <Input
                  label={translate(lang, 'Quantity')}
                  placeholder={translate(
                    lang,
                    '_max_allowed_value',
                    availableQty,
                  )}
                  type='number'
                  value={numToStr(formData.quantity)}
                  onChange={e => updateForm('quantity', e.target.value)}
                  disabled={onlyView}
                />
              </div>
            </div>
          )}
          {!!formData.time_tracks && (
            <TimeTrackInput
              data={formData.time_tracks}
              onChange={t => updateForm('time_tracks', t)}
              baseDate={formData.manage_date}
              disabled={onlyView}
            />
          )}
          <SelectBoat
            className='mb-17'
            value={formData.boat_id}
            onChange={v => updateForm('boat_id', v)}
            disabled={onlyView}
          />
        </div>
        {!!manageCycle && (
          <div className='mt-32 mb-32'>
            <div className='d-flex align-center mb-7'>
              <Subtitle color='black-1' align='left' size={4} fontWeight={600}>
                {translate(lang, 'Line depth manage')}
              </Subtitle>
            </div>
            <Input
              label=''
              placeholder={translate(lang, '_enter_line_depth')}
              type='number'
              unit='m'
              value={numToStr(depth)}
              onChange={e =>
                setDepth(
                  e.target.value.length <= 0 ? null : Number(e.target.value),
                )
              }
            />
          </div>
        )}
      </div>
      {!updateId && (
        <TaggingManageForm
          className='mb-17 mt-17'
          lineData={curLine}
          tagIDs={formData.tagged_ids}
          onChange={ids => updateForm('tagged_ids', ids)}
        />
      )}
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={handleConfirmClick}
          disabled={disabled || onlyView}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default FloatManageModal;
